import { inject, Injectable } from "@angular/core";
import { UserApi } from "../Api/UserApi";
import { UserState } from "./UserState";
import { SearchUserRequest } from "../Dto/UserSearchRequest";
import { ManageResetPasswordRequest } from "../Dto/UserManageResetPasswordRequest";
import { UserListItem } from "../Dto/UserModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class UserEffects {
  private readonly userApi = inject(UserApi);
  private readonly userState = inject(UserState);

  async searchUsers(params: Partial<SearchUserRequest> = {}): Promise<void> {
    const defaultParams: SearchUserRequest = {
      page: 1,
      limit: 10,
      ...params,
    };

    this.userState.setLoading(true);
    this.userState.setError(null);
    this.userState.setSearchParams(defaultParams);

    try {
      const response = await this.userApi.searchUsers(defaultParams);
      this.userState.setUsers(response.data, response.pagination);
    } catch (error: unknown) {
      this.userState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
    } finally {
      this.userState.setLoading(false);
    }
  }

  async createUser(payload: import("../Dto/UserCreateRequest").CreateUserRequest): Promise<UserListItem> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      const createdUser = await this.userApi.createUser(payload);
      this.userState.addUserToState(createdUser);
      return createdUser;
    } catch (exception: unknown) {
      this.userState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }

  async updateUser(userId: string, userData: Partial<UserListItem>): Promise<UserListItem | null> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      const updatedUser = await this.userApi.updateUser(userId, userData);
      this.userState.updateUserInState(userId, updatedUser);
      return updatedUser;
    } catch (exception: unknown) {
      this.userState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }

  async updateProfile(userId: string, profileData: Partial<UserListItem>): Promise<boolean> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      await this.userApi.updateProfile(userId, profileData);
      this.userState.updateUserInState(userId, profileData);
      return true;
    } catch (error: unknown) {
      this.userState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      throw createBackendError(error, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }

  async getManageProfile(userId: string): Promise<UserListItem | null> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      return await this.userApi.getManageProfile(userId);
    } catch (exception: unknown) {
      this.userState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      return null;
    } finally {
      this.userState.setLoading(false);
    }
  }

  async updateManageProfile(userId: string, profileData: Partial<UserListItem>): Promise<UserListItem> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      const updatedUser = await this.userApi.updateManageProfile(userId, profileData);
      this.userState.updateUserInState(userId, updatedUser);
      return updatedUser;
    } catch (exception: unknown) {
      // Removed logging
      this.userState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }

  async resetPasswordByAdmin(userId: string, payload: ManageResetPasswordRequest): Promise<{ success: boolean; message: string }> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      const response = await this.userApi.resetPasswordByAdmin(userId, payload);
      return { success: true, message: response.message };
    } catch (exception: unknown) {
      const message = getBackendErrorMessage(exception, "An unexpected error occurred");
      this.userState.setError(message);
      return { success: false, message };
    } finally {
      this.userState.setLoading(false);
    }
  }

  async deleteImage(): Promise<boolean> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      await this.userApi.deleteImage();
      return true;
    } catch (error: unknown) {
      this.userState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      throw createBackendError(error, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }

  async deleteManageUser(userId: string): Promise<boolean> {
    this.userState.setLoading(true);
    this.userState.setError(null);

    try {
      await this.userApi.deleteManageUser(userId);
      this.userState.removeUserFromState(userId);
      return true;
    } catch (exception: unknown) {
      this.userState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.userState.setLoading(false);
    }
  }
}
